/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
// false positive no-unnecessary-type-assertion
// https://typescript-eslint.io/rules/no-unnecessary-condition/#possibly-undefined-indexed-access
import React, { useCallback, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { compact, isEqual, isNil, noop } from 'lodash';
import { AlternateModalHeader, ModalFooter, NumberInput, type ReorderableGroup } from 'venn-components';
import {
  CheckboxInput,
  CheckboxTree,
  Collapsible,
  ColorUtils,
  ExternalActivityListener,
  flattenTree,
  GetColor,
  Icon,
  Loading,
  portalMenuIgnoreActivityClassName,
  RelativePortal,
  Subtitle2,
  Tooltip,
  type TreeNode,
} from 'venn-ui-kit';
import { type Subject, type SubjectInputId, type WorkspaceFeeLimits, workspaceFeeLimitsState } from 'venn-state';
import { excludedFees, requestSubjects } from 'venn-state';
import { SubjectGroupRowWrapper, SubjectItem } from './SubjectGroupRow';
import { useFees } from './useFees';
import Measure from 'react-measure';
import {
  getFullName,
  getStudioRequestSubjectCashNodes,
  isCashNode,
  isOptionNode,
  isStrategyNode,
  Numbers,
  useHasFF,
} from 'venn-utils';
import type { Portfolio } from 'venn-api';
import { cn } from '@/lib/utils';
import { CashIcon } from '../../../../../../../venn-components/src/common/CashIcon';
import { OptionIcon } from '../../../../../../../venn-components/src/common/OptionIcon';
import styled from 'styled-components';

interface ManageFeesModalProps {
  subjectGroups: ReorderableGroup<SubjectInputId, { key: string; value: Subject }>[];
  onClose: () => void;
  relativeElement?: Element;
  topOffset?: number;
  leftOffset?: number;
}

interface InnerManageFeesModalProps {
  subjectGroups: ReorderableGroup<SubjectInputId, { key: string; value: Subject }>[];
  onClose: () => void;
  advisoryFeeConfig: WorkspaceFeeLimits | undefined;
}

const FEE_MIN_PCT = 0;
const FEE_MAX_PCT = 100;
const FEE_MAX = FEE_MAX_PCT / 100;
const FEE_MIN = FEE_MIN_PCT / 100;
const subjectItemContainerClassname = 'subject-item-container';
const subjectRowClassname = 'subject-row-container';

const ManageFeesModal = ({ subjectGroups, onClose, advisoryFeeConfig }: InnerManageFeesModalProps) => {
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const hasDefaultRlAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');
  const groupIds = subjectGroups.map(({ id }) => id);
  const [baselineFees, setBaselineFees] = useFees(groupIds);
  const [fees, setFees] = useState(baselineFees);
  const baselineExclusions = useRecoilValue(excludedFees(groupIds));
  const [selectedExclusions, setSelectedExclusions] = useState<Record<SubjectInputId, string[][]>>(baselineExclusions);

  const inputSupportsFees: Record<SubjectInputId, boolean[]> = {};
  for (const group of subjectGroups) {
    inputSupportsFees[group.id] = group.reorderableItems.map((item, itemIndex) => {
      if (!fees[group.id]) return false;
      return (
        (hasAdvisoryFeesForRlInvestmentsFF && !isNil(item.value.fundId)) ||
        !isNil(item.value.portfolioId) ||
        !!fees[group.id]![itemIndex]
      );
    });
  }

  const {
    canSave,
    saveDisabledTooltipMessage: saveDisabledTooltip,
    errorBannerMessage: errorBanner,
  } = getCanSaveStatusAndOptionalError(
    fees,
    inputSupportsFees,
    baselineFees,
    selectedExclusions,
    baselineExclusions,
    advisoryFeeConfig,
    hasDefaultRlAdvisoryFeesFF,
  );

  return (
    <>
      <div
        className={cn([
          'max-h-[70vh] overflow-y-auto',
          `[&_.${subjectRowClassname}]:p-[8px] [&_.${subjectRowClassname}]:border-t [&_.${subjectRowClassname}]:border-solid`,
          `[&_.${subjectRowClassname}]:border-t-venn-grey`,
        ])}
      >
        {subjectGroups.map((group) => (
          <SubjectGroupRow
            key={group.id}
            group={group}
            fees={fees}
            setFees={setFees}
            selectedKeys={selectedExclusions[group.id] ?? []}
            setSelectedKeys={(newKeys) =>
              setSelectedExclusions({
                ...selectedExclusions,
                [group.id]: newKeys,
              })
            }
          />
        ))}
        {errorBanner && (
          <StyledBannerMessage className="mt-[24px]">
            <Icon type="triangle-exclamation" className="text-venn-error" /> {errorBanner}
          </StyledBannerMessage>
        )}
      </div>

      <ModalFooter
        className={errorBanner ? 'mt-0' : undefined}
        primaryLabel="Save"
        onPrimaryClick={() => {
          setBaselineFees(fees, selectedExclusions);
          onClose();
        }}
        primaryDisabled={!canSave}
        primaryTooltip={saveDisabledTooltip}
        onCancel={onClose}
      />
    </>
  );
};

interface SubjectGroupRowProps {
  group: ReorderableGroup<SubjectInputId, { key: string; value: Subject }>;
  fees: Record<SubjectInputId, number[]>;
  setFees: (fees: Record<SubjectInputId, number[]>) => void;
  selectedKeys: string[][];
  setSelectedKeys: (keys: string[][]) => void;
}

export const getNodeContent = (portfolio: Portfolio): React.ReactNode => {
  const isCash = isCashNode(portfolio);
  const isOption = isOptionNode(portfolio);
  const optionData = isOption ? portfolio.fund?.optionData : undefined;
  const name = getFullName(portfolio);

  return (
    <>
      {name} {isCash && <CashIcon isNegativeAssets={(portfolio.netAssets ?? 0) < 0} />}
      {optionData && <OptionIcon optionType={optionData.optionType} />}
    </>
  );
};

const portfolioToTreeNode = (portfolio: Portfolio): TreeNode<React.ReactNode> | undefined => {
  const { children, id } = portfolio;
  const isStrategy = isStrategyNode(portfolio);
  const childNodes = compact(children.map(portfolioToTreeNode));
  if (isStrategy && childNodes.length === 0) {
    return undefined;
  }
  const isCash = isCashNode(portfolio);
  return {
    children: childNodes,
    data: getNodeContent(portfolio),
    key: id.toString(10),
    selectedOverride: isCash ? false : undefined,
    tooltip: isCash
      ? (portfolio.netAssets ?? 0) < 0
        ? 'Unable to apply fees to cash borrowing.'
        : 'Unable to apply fees to cash holding.'
      : undefined,
  };
};

const SubjectGroupRow = ({ group, fees, setFees, selectedKeys, setSelectedKeys }: SubjectGroupRowProps) => {
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const groupsRequestSubjects = useRecoilValue(requestSubjects(group.reorderableItems.map((item) => item.value)));
  const portfolioNodes = groupsRequestSubjects.map((subject) =>
    subject.portfolio ? portfolioToTreeNode(subject.portfolio) : undefined,
  );

  const rowRenderer: React.ComponentProps<typeof CheckboxTree<React.ReactNode>>['rowRenderer'] = useCallback(
    ({ level, key, data, isLeaf, selected, onSelect, selectedOverride, tooltip }) => (
      <div
        className="my-[2px] flex text-[1rem] items-center"
        style={{
          fontWeight: isLeaf ? 'bold' : undefined,
          marginLeft: `${16 * (level - 1)}px`,
        }}
      >
        {isLeaf ? (
          <>
            <CheckboxInput
              id={key}
              checked={selectedOverride ?? !selected}
              onChange={() => onSelect(key)}
              readOnly={selectedOverride !== undefined}
              disabled={selectedOverride !== undefined}
            />
            <label
              className={cn('px-[3px] font-normal', selectedOverride !== undefined ? ' text-venn-grey-50' : '')}
              htmlFor={key}
            >
              <Tooltip content={tooltip} usePortal>
                {data}
              </Tooltip>
            </label>
          </>
        ) : (
          data
        )}
      </div>
    ),
    [],
  );

  return (
    <SubjectGroupRowWrapper
      key={group.id}
      readonly
      noHoverStyles
      groupId={group.id}
      trackGroupChanged={noop}
      className={subjectRowClassname}
      deletable={false}
    >
      {group.reorderableItems.map((item, itemIndex) => {
        const requestSubject = groupsRequestSubjects[itemIndex];
        const isHistoricalPortfolio = requestSubject?.portfolio?.historical;

        if (isHistoricalPortfolio) {
          return null;
        }

        const cashNodes = getStudioRequestSubjectCashNodes(requestSubject);
        const numExcluded = cashNodes.length + (selectedKeys[itemIndex]?.length ?? 0);

        // If a fee currently exists for an investment, always allow them to edit it
        const shouldShow =
          fees[group.id] &&
          ((hasAdvisoryFeesForRlInvestmentsFF && !isNil(item.value.fundId)) ||
            !isNil(item.value.portfolioId) ||
            fees[group.id]![itemIndex]);

        if (!shouldShow) {
          return null;
        }

        return (
          <>
            <div
              className={cn([
                'min-w-[400px] flex justify-between',
                `[&_.${subjectItemContainerClassname}]:py-0 [&_.${subjectItemContainerClassname}]:pr-[15px] [&_.${subjectItemContainerClassname}]:pl-0`,
              ])}
              key={item.key}
            >
              <SubjectItem
                readonly
                hideAllocatorLauncher
                hideSubjectActions
                subject={item.value}
                groupId={group.id}
                onDelete={noop}
                onSwap={noop}
                onEditFees={noop}
                onEditTaxRates={noop}
                onFilter={noop}
                className={subjectItemContainerClassname}
              />
              <NumberInput
                className="min-w-[124px]"
                align="right"
                inputClassName="pr-[4px]"
                label={`${requestSubject!.name} fees`}
                data-testid="qa-fees-input"
                min={FEE_MIN_PCT}
                max={FEE_MAX_PCT}
                precision={2}
                step={0.1}
                onChange={(value: number) => {
                  setFees({
                    ...fees,
                    [group.id]: [
                      ...fees[group.id]!.slice(0, itemIndex),
                      value / 100,
                      ...fees[group.id]!.slice(itemIndex + 1),
                    ],
                  });
                }}
                unitSymbol="%"
                value={(fees[group.id]![itemIndex] ?? 0) * 100}
                controlled
              />
            </div>
            {portfolioNodes[itemIndex] && (
              <Collapsible label={`Exclude investments ${numExcluded ? `(${numExcluded} excluded)` : ''}`}>
                <span className="text-venn-grey-80 text-[1rem]">{FEE_EXCLUSION_INSTRUCTIONS}</span>
                <CheckboxTree
                  selectedKeys={selectedKeys[itemIndex] ?? []}
                  setSelectedKeys={(newSelectedKeys) =>
                    setSelectedKeys([
                      ...selectedKeys.slice(0, itemIndex),
                      newSelectedKeys,
                      ...selectedKeys.slice(itemIndex + 1),
                    ])
                  }
                  rows={flattenTree(portfolioNodes[itemIndex]!).slice(1)}
                  rowRenderer={rowRenderer}
                />
              </Collapsible>
            )}
          </>
        );
      })}
    </SubjectGroupRowWrapper>
  );
};

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
export default ({ topOffset, leftOffset, subjectGroups, onClose, relativeElement }: ManageFeesModalProps) => {
  const workspaceFeeLimits = useRecoilValue(workspaceFeeLimitsState);
  const [height, setHeight] = useState<number | undefined>(undefined);
  return (
    <Measure
      bounds
      onResize={({ bounds }) => {
        setHeight(bounds?.height);
      }}
    >
      {({ measureRef }) => (
        <div>
          <RelativePortal
            leftOffset={leftOffset}
            topOffset={topOffset}
            expectedHeight={height}
            relativeElement={relativeElement}
          >
            <ExternalActivityListener
              ignoreActivityFromClassName={portalMenuIgnoreActivityClassName}
              onExternalActivity={onClose}
            >
              <PopupContainer ref={measureRef} wide>
                <AlternateModalHeader>Advisory Fees</AlternateModalHeader>
                <div className="py-[8px] px-[16px] flex justify-between">
                  <Subtitle2>Subjects</Subtitle2>
                  <Subtitle2>Fees</Subtitle2>
                </div>
                <React.Suspense fallback={<Loading />}>
                  <ManageFeesModal
                    subjectGroups={subjectGroups}
                    onClose={onClose}
                    advisoryFeeConfig={workspaceFeeLimits}
                  />
                </React.Suspense>
              </PopupContainer>
            </ExternalActivityListener>
          </RelativePortal>
        </div>
      )}
    </Measure>
  );
};

const PopupContainer = ({
  children,
  className,
  wide,
}: React.BaseHTMLAttributes<HTMLDivElement> & React.RefAttributes<HTMLDivElement> & { wide: boolean }) => (
  <div
    className={cn([
      'bg-venn-white flex flex-col justify-between min-h-[160px] border-4 border-solid border-venn-primary-main',
      wide ? 'min-w-[700px] max-w-[700px]' : 'min-w-[300px]',
      className,
    ])}
  >
    {children}
  </div>
);

const getVisibleFees = (
  feesGroup: Record<string, number[]>,
  inputSupportsFees: Partial<Record<SubjectInputId, boolean[]>>,
) =>
  Object.entries(feesGroup).flatMap(([id, values]) =>
    values.filter((_, i) => inputSupportsFees[id as SubjectInputId]?.[i]),
  );

const getFeeRangeError = (
  fees: number[],
  minFee: number,
  maxFee: number,
): { message: string; saveMessage?: string } | undefined => {
  if (!fees.some((fee) => fee > maxFee / 100.0 || fee < minFee / 100.0)) {
    return undefined;
  }

  const formattedMinFee = Numbers.safeFormatPercentage(minFee / 100);
  const formattedMaxFee = Numbers.safeFormatPercentage(maxFee / 100);
  const baseMessage = `Fee must be within the workspace limit of ${formattedMinFee} to ${formattedMaxFee}.`;

  return {
    message: `${baseMessage} Please change and apply the fee below.`,
    saveMessage: `${baseMessage} Please change and apply the fee above.`,
  };
};

export const getCanSaveStatusAndOptionalError = (
  feesGroup: Record<SubjectInputId, number[]>,
  inputSupportsFees: Record<SubjectInputId, boolean[]>,
  baselineFeesGroup: Record<SubjectInputId, number[]>,
  exclusions: Record<SubjectInputId, string[][]>,
  baselineExclusions: Record<SubjectInputId, string[][]>,
  workspaceFeeLimits: WorkspaceFeeLimits | undefined,
  hasDefaultRlAdvisoryFeesFF: boolean,
): {
  isValid: boolean;
  canSave: boolean;
  saveDisabledTooltipMessage: string | undefined;
  errorBannerMessage: string | undefined;
} => {
  const minFee = workspaceFeeLimits?.minFee;
  const maxFee = workspaceFeeLimits?.maxFee;
  const validateRange = hasDefaultRlAdvisoryFeesFF && minFee !== undefined && maxFee !== undefined;

  const visibleBaselineFees = getVisibleFees(baselineFeesGroup, inputSupportsFees);
  const visibleFees = getVisibleFees(feesGroup, inputSupportsFees);

  const baselineFeeError = validateRange ? getFeeRangeError(visibleBaselineFees, minFee, maxFee) : undefined;

  const getCurrentFeeError = (): { message: string; saveMessage?: string } | undefined => {
    if (visibleFees.some((fee) => fee > FEE_MAX || fee < FEE_MIN)) {
      return {
        message: `Advisory Fee value must be between ${FEE_MIN_PCT} and ${FEE_MAX_PCT}%`,
      };
    }

    return validateRange ? getFeeRangeError(visibleFees, minFee, maxFee) : undefined;
  };
  const currentFeeError = getCurrentFeeError();

  const noChangesError =
    isEqual(feesGroup, baselineFeesGroup) && isEqual(exclusions, baselineExclusions) ? 'No changes to save' : undefined;

  return {
    isValid: !currentFeeError,
    canSave: !currentFeeError && !noChangesError,
    saveDisabledTooltipMessage: currentFeeError?.saveMessage ?? currentFeeError?.message ?? noChangesError,
    errorBannerMessage: currentFeeError?.message ?? baselineFeeError?.message,
  };
};

const FEE_EXCLUSION_INSTRUCTIONS = `
Uncheck investments to exclude them from the applied advisory fee. When investments are excluded, a
footnote will be added to each page directing readers to the Disclosures page, where all excluded
investments will be detailed.
`;

const StyledBannerMessage = styled.span`
  display: flex;
  padding: 8px 12px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  font-size: 12px;
  background-color: ${ColorUtils.opacifyFrom(GetColor.Error, 0.1)};
`;
